import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { ThemeToggle } from './components/theme-toggle/ThemeToggle';
import { CronParserPage } from './pages/CronParserPage';
import { JsonTableConverterPage } from './pages/JsonTableConverterPage';
import { JsonYamlConverterPage } from './pages/JsonYamlConverterPage';
import { NumberBaseConverterPage } from './pages/NumberBaseConverterPage';
import { DateConverterPage } from './pages/DateConverterPage';
import { Base64TextEncoderPage } from './pages/Base64TextEncoderPage';
import { QRCodePage } from './pages/QRCodePage';
import { UrlEncoderPage } from './pages/UrlEncoderPage';
import { Base64ImageEncoderPage } from './pages/Base64ImageEncoderPage';
import { JwtTokenEncoderPage } from './pages/JwtTokenEncoderPage';
import { HtmlEntityEncoderPage } from './pages/HtmlEntityEncoderPage';
import { GzipCompressionPage } from './pages/GzipCompressionPage';
import { CertificateDecoderPage } from './pages/CertificateDecoderPage';
import { JsonFormatterPage } from './pages/JsonFormatterPage';
import { SqlFormatterPage } from './pages/SqlFormatterPage';
import { XmlFormatterPage } from './pages/XmlFormatterPage';
import HashGeneratorPage from './pages/HashGeneratorPage';
import LoremIpsumPage from './pages/LoremIpsumPage';
import PasswordGeneratorPage from './pages/PasswordGeneratorPage';
import UuidGeneratorPage from './pages/UuidGeneratorPage';
import { ColorBlindnessSimulatorPage } from './pages/ColorBlindnessSimulatorPage';
import { ImageConverterPage } from './pages/ImageConverterPage';
import { JSONPathTesterPage } from './pages/JSONPathTesterPage';
import { RegExTesterPage } from './pages/RegExTesterPage';
import { XMLTesterPage } from './pages/XMLTesterPage';
import { TextEscapePage } from './pages/TextEscapePage';
import { ListComparePage } from './pages/ListComparePage';
import { MarkdownPreviewPage } from './pages/MarkdownPreviewPage';
import { TextAnalyzerPage } from './pages/TextAnalyzerPage';
import { TextComparePage } from './pages/TextComparePage';
import { HomePage } from './pages/HomePage';
import './App.css';

const tools = [
  // Converters Tools
  { path: '/cron-parser', name: 'Cron Parser', category: 'Converters' },
  { path: '/date-converter', name: 'Date Converter', category: 'Converters' },
  { path: '/json-table-converter', name: 'JSON > Table', category: 'Converters' },
  { path: '/json-yaml-converter', name: 'JSON <> YAML', category: 'Converters' },
  { path: '/number-base-converter', name: 'Number Base', category: 'Converters' },

  // Encoders & Decoders Tools
  { path: '/base64-text-encoder', name: 'Base64 Text', category: 'Encoders & Decoders' },
  { path: '/qr-code', name: 'QR Code', category: 'Encoders & Decoders' },
  { path: '/url-encoder', name: 'URL', category: 'Encoders & Decoders' },
  { path: '/base64-image-encoder', name: 'Base64 Image', category: 'Encoders & Decoders' },
  { path: '/jwt-token-encoder', name: 'JWT Token', category: 'Encoders & Decoders' },
  { path: '/html-entity-encoder', name: 'HTML Entity', category: 'Encoders & Decoders' },
  { path: '/gzip-compression', name: 'GZip', category: 'Encoders & Decoders' },
  { path: '/certificate-decoder', name: 'Certificate', category: 'Encoders & Decoders' },

  // Formatters Tools
  { path: '/json-formatter', name: 'JSON', category: 'Formatters' },
  { path: '/sql-formatter', name: 'SQL', category: 'Formatters' },
  { path: '/xml-formatter', name: 'XML', category: 'Formatters' },

  // Generators Tools
  { path: '/hash-generator', name: 'Hash/Checksum', category: 'Generators' },
  { path: '/lorem-ipsum', name: 'Lorem Ipsum', category: 'Generators' },
  { path: '/password-generator', name: 'Password', category: 'Generators' },
  { path: '/uuid-generator', name: 'UUID', category: 'Generators' },

  // Graphic Tools
  { path: '/color-blindness-simulator', name: 'Color Blindness', category: 'Graphic' },
  { path: '/image-converter', name: 'Image Converter', category: 'Graphic' },

  // Testers Tools
  { path: '/jsonpath-tester', name: 'JSONPath', category: 'Testers' },
  { path: '/regex-tester', name: 'RegEx', category: 'Testers' },
  { path: '/xml-tester', name: 'XML', category: 'Testers' },

  // Text Tools
  { path: '/text-escape', name: 'Escape/Unescape', category: 'Text' },
  { path: '/list-compare', name: 'List Compare', category: 'Text' },
  { path: '/markdown-preview', name: 'Markdown Preview', category: 'Text' },
  { path: '/text-analyzer', name: 'Analyzer & Utilities', category: 'Text' },
  { path: '/text-compare', name: 'Compare', category: 'Text' },
];

function Sidebar({ searchQuery, isOpen, onClose }: {
  searchQuery: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const location = useLocation();
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
  'Encoders & Decoders': true,
  'Converters': true,
  'Formatters': true,
  'Generators': true,
  'Graphic': true,
  'Testers': true,
  'Text': true
});

  // Auto-expand all categories when searching
  useEffect(() => {
    if (searchQuery) {
      const allCategories = Array.from(new Set(tools.map(tool => tool.category)));
      setExpandedCategories(prev => {
        const updated = { ...prev };
        allCategories.forEach(category => {
          updated[category] = true;
        });
        return updated;
      });
    }
  }, [searchQuery]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  // Filter tools based on search query
  const filteredTools = tools.filter(tool =>
    tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedTools = filteredTools.reduce((acc: Record<string, typeof tools>, tool) => {
    if (!acc[tool.category]) {
      acc[tool.category] = [];
    }
    acc[tool.category].push(tool);
    return acc;
  }, {});

  return (
    <div className={`sidebar-layout ${isOpen ? 'sidebar-open' : ''}`}>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          aria-label="Close menu overlay"
        />
      )}
      <nav className={`sidebar ${isOpen ? 'open' : ''}`}>
        <button
          className="sidebar-close"
          onClick={onClose}
          aria-label="Close menu"
        >
          ×
        </button>
        <div className="sidebar-header">
          <h2>WebDevToys</h2>
          <p>Developer Tools</p>
        </div>

        <div className="sidebar-content">
          {searchQuery && filteredTools.length === 0 ? (
            <div className="no-results">
              <p>No tools found for "{searchQuery}"</p>
            </div>
          ) : (
            Object.entries(groupedTools).map(([category, categoryTools]) => (
              <div key={category} className="category-section">
                <button
                  className="category-toggle"
                  onClick={() => toggleCategory(category)}
                >
                  <span className={`arrow ${expandedCategories[category] ? 'expanded' : ''}`}>▶</span>
                  {category}
                </button>

                {expandedCategories[category] && (
                  <ul className="tool-list">
                    {categoryTools.map((tool: any) => (
                      <li key={tool.path}>
                        <Link
                          to={tool.path}
                          className={`tool-link ${location.pathname === tool.path ? 'active' : ''}`}
                          onClick={onClose}
                        >
                          {tool.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>

        <div className="sidebar-footer">
          <a
            href="https://github.com/holywen/webdevtoys-community/issues"
            target="_blank"
            rel="noopener noreferrer"
            className="report-issue-link"
            onClick={onClose}
          >
            🐛 Report Issue
          </a>
        </div>
      </nav>

      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Encoders & Decoders Tools */}
          <Route path="/base64-text-encoder" element={<Base64TextEncoderPage />} />
          <Route path="/qr-code" element={<QRCodePage />} />
          <Route path="/url-encoder" element={<UrlEncoderPage />} />
          <Route path="/base64-image-encoder" element={<Base64ImageEncoderPage />} />
          <Route path="/jwt-token-encoder" element={<JwtTokenEncoderPage />} />
          <Route path="/html-entity-encoder" element={<HtmlEntityEncoderPage />} />
          <Route path="/gzip-compression" element={<GzipCompressionPage />} />
          <Route path="/certificate-decoder" element={<CertificateDecoderPage />} />
          {/* Converters Tools */}
          <Route path="/cron-parser" element={<CronParserPage />} />
          <Route path="/date-converter" element={<DateConverterPage />} />
          <Route path="/json-table-converter" element={<JsonTableConverterPage />} />
          <Route path="/json-yaml-converter" element={<JsonYamlConverterPage />} />
          <Route path="/number-base-converter" element={<NumberBaseConverterPage />} />
          {/* Formatters Tools */}
          <Route path="/json-formatter" element={<JsonFormatterPage />} />
          <Route path="/sql-formatter" element={<SqlFormatterPage />} />
          <Route path="/xml-formatter" element={<XmlFormatterPage />} />
          {/* Generators Tools */}
          <Route path="/hash-generator" element={<HashGeneratorPage />} />
          <Route path="/lorem-ipsum" element={<LoremIpsumPage />} />
          <Route path="/password-generator" element={<PasswordGeneratorPage />} />
          <Route path="/uuid-generator" element={<UuidGeneratorPage />} />
          {/* Graphic Tools */}
          <Route path="/color-blindness-simulator" element={<ColorBlindnessSimulatorPage />} />
          <Route path="/image-converter" element={<ImageConverterPage />} />
          {/* Testers Tools */}
          <Route path="/jsonpath-tester" element={<JSONPathTesterPage />} />
          <Route path="/regex-tester" element={<RegExTesterPage />} />
          <Route path="/xml-tester" element={<XMLTesterPage />} />
          {/* Text Tools */}
          <Route path="/text-escape" element={<TextEscapePage />} />
          <Route path="/list-compare" element={<ListComparePage />} />
          <Route path="/markdown-preview" element={<MarkdownPreviewPage />} />
          <Route path="/text-analyzer" element={<TextAnalyzerPage />} />
          <Route path="/text-compare" element={<TextComparePage />} />
        </Routes>
      </main>
    </div>
  );
}

function AppHeader({ onSearchChange, onMenuToggle }: {
  onSearchChange: (query: string) => void;
  onMenuToggle: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearchChange(query);
  };

  return (
    <header className="app-header">
      <button
        className="menu-toggle"
        onClick={onMenuToggle}
        aria-label="Toggle menu"
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>
      <div className="search-container">
        <input
          type="text"
          placeholder="Search tools..."
          value={searchQuery}
          onChange={handleChange}
          className="search-input"
        />
      </div>
      <div className="theme-toggle-container">
        <ThemeToggle />
      </div>
    </header>
  );
}

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <ThemeProvider>
      <Router>
        <div className="app-container">
          <AppHeader
            onSearchChange={setSearchQuery}
            onMenuToggle={toggleSidebar}
          />
          <Sidebar
            searchQuery={searchQuery}
            isOpen={isSidebarOpen}
            onClose={closeSidebar}
          />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;